import { Info, PaintBucket } from "lucide-react";
import { useState } from "react";

import { useProductColors } from "@design-lab/entities/product";
import { UnionIcon } from "@design-lab/entities/quality-tabs";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { trackImageEditToolUsed, trackImageEditToolError } from "@design-lab/shared/lib/analytics";
import { cn } from "@design-lab/shared/lib/tailwindcss";
import { Button } from "@design-lab/shared/ui/design-system/Button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@design-lab/shared/ui/tooltip";
import { processNegativeSpace } from "@design-lab/widgets/artboard/lib/negativeSpaceProcessor";
import { useRefineHistoryContext } from "@design-lab/widgets/artboard/model/RefineHistoryContext";

import { ColorPickerPopup } from "./ColorPickerPopup";

type NegativeSpaceToolProps = {
  isActive: boolean;
  artworkId?: string;
};

export function NegativeSpaceTool({ isActive, artworkId }: NegativeSpaceToolProps) {
  const [showTooltip, setShowTooltip] = useState(true);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const { selectedColor } = useProductColors();
  const [selectedColorForTool, setSelectedColorForTool] = useState(selectedColor?.hex);
  const { isDesktop } = useResponsive();
  const { currentImageData, pushOperation, currentOperation } = useRefineHistoryContext();

  if (!isActive) return null;

  const handlePickColor = () => {
    setShowColorPicker(true);
    setShowTooltip(false);
  };

  const handleBlendColor = async () => {
    if (!currentImageData || !selectedColorForTool || !currentOperation?.metadata?.dimensions) {
      console.warn("Missing required data:", {
        hasImageData: !!currentImageData,
        hasColor: !!selectedColorForTool,
        hasDimensions: !!currentOperation?.metadata?.dimensions
      });
      return;
    }

    setShowTooltip(false);
    setShowColorPicker(false);
    setIsProcessing(true);

    try {
      // Convert base64 image data to ImageData
      const img = new Image();
      img.src = currentImageData;

      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
      });

      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");

      if (!ctx) {
        throw new Error("Failed to get canvas context");
      }

      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

      // Process negative space
      const result = processNegativeSpace({
        imageData,
        targetHexColor: selectedColorForTool
      });

      // Convert result back to base64
      const resultCanvas = document.createElement("canvas");
      resultCanvas.width = result.imageData.width;
      resultCanvas.height = result.imageData.height;
      const resultCtx = resultCanvas.getContext("2d");

      if (!resultCtx) {
        throw new Error("Failed to get result canvas context");
      }

      resultCtx.putImageData(result.imageData, 0, 0);
      const processedImageData = resultCanvas.toDataURL("image/png");

      // Push to history
      pushOperation({
        type: "negative-space",
        imageData: processedImageData,
        metadata: {
          dimensions: currentOperation.metadata.dimensions,
          selectedColor: selectedColorForTool,
          removedPixels: result.stats.removedPixelCount,
          eligiblePixels: result.stats.eligiblePixelCount
        },
        timestamp: Date.now()
      });

      if (artworkId) {
        trackImageEditToolUsed(artworkId, "negative_space", {
          hex_color: selectedColorForTool
        });
      }
    } catch (error) {
      console.error("Error processing negative space:", error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      trackImageEditToolError("negative_space", errorMessage, {
        error,
        ...(artworkId && { artworkId }),
        component: "NegativeSpaceTool"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCloseTooltip = () => {
    setShowTooltip(false);
  };

  const handleColorSelect = (colorHex: string) => {
    setSelectedColorForTool(colorHex);
  };

  const handleCloseColorPicker = () => {
    setShowColorPicker(false);
  };

  return (
    <>
      <div
        className={cn(
          "relative left-1/2 z-20 flex -translate-x-1/2 gap-1 rounded-lg bg-gray-200 p-1",
          isDesktop ? "bottom-22" : "bottom-26"
        )}
        style={{ position: "absolute" }}
      >
        <Button
          data-testid="negative-space-pick-color"
          variant="outline"
          className={cn(
            "flex items-center gap-2 rounded-lg bg-white px-4 py-2 shadow-lg",
            showColorPicker && "border border-blue-700"
          )}
          onPress={handlePickColor}
          size="lg"
        >
          <PaintBucket className="size-5" />
          <span className="font-medium">Pick Color</span>
          <div
            className="size-4 rounded-full border border-gray-300"
            style={{ backgroundColor: selectedColorForTool }}
          />
        </Button>

        <Tooltip open={showTooltip} onOpenChange={setShowTooltip}>
          <Button
            data-testid="negative-space-jiffy-magic"
            className="bg-cta-gradient hover:bg-cta-gradient-hover flex items-center gap-2 rounded-lg px-4 py-2 text-white shadow-lg"
            onPress={handleBlendColor}
            size="lg"
            isDisabled={isProcessing || !selectedColorForTool}
          >
            {isProcessing ? (
              <>
                <div className="size-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                <span className="font-medium">Processing...</span>
              </>
            ) : (
              <>
                <UnionIcon className="size-5" />
                <span className="font-medium">Blend Color</span>
                <TooltipTrigger asChild data-testid="negative-space-jiffy-magic-tooltip">
                  <div className="flex items-center gap-2">
                    <Info className="size-4" />
                  </div>
                </TooltipTrigger>
              </>
            )}
          </Button>
          <TooltipContent
            side="top"
            className="max-w-xs bg-blue-700"
            onClose={handleCloseTooltip}
            sideOffset={8}
            arrowClassName="bg-blue-700 fill-blue-700"
          >
            Automatically removes parts of the image that match the color of your selected apparel.
          </TooltipContent>
        </Tooltip>
        <ColorPickerPopup
          isOpen={showColorPicker}
          onClose={handleCloseColorPicker}
          onColorSelect={handleColorSelect}
          selectedColor={selectedColorForTool}
        />
      </div>
    </>
  );
}
